import { Autocomplete } from 'stimulus-autocomplete';

const CONVERSION_USER_TYPES = ['Users::Guest', 'Users::Customer'];
const FILLABLE_FIELDS = [
  { name: 'firstName', attr: 'first-name' },
  { name: 'lastName', attr: 'last-name' },
  { name: 'phone', attr: 'phone' },
];

export default class extends Autocomplete {
  static targets = [
    'input',
    'results',
    'selectedUserId',
    'selectedUserCard',
    'emailField',
    'firstNameField',
    'lastNameField',
    'phoneField',
    'searchSubmitButton',
    'missingFieldsNotice',
    'memberOfThisCompanyNotice',
    'memberOfOtherCompanyNotice',
    'existingUserNotice',
    'newUserSection',
    'searchSection',
  ];

  static values = {
    addLabel: String,
    convertAccountLabel: String,
  };

  connect() {
    super.connect();

    this.inputTarget.addEventListener('input', this.onSearchInput);

    if (this.hasNewUserSectionTarget) {
      this.newUserSectionTarget.querySelectorAll('input').forEach((input) => {
        input.addEventListener('input', this.onNewUserInput);
      });
    }

    if (this.hasSelectedUserCardTarget && !this.selectedUserCardTarget.hidden) {
      this.hideNewUserSection();
      this.syncNoticesAndSubmit();
    }
  }

  onSearchInput = () => {
    if (this.isCommitting) return;
    if (!this.hasSelectedUserIdTarget || !this.selectedUserIdTarget.value) return;

    this.dismissSelectedUser();
  };

  onNewUserInput = () => {
    if (this.isCommitting) return;

    this.clearSelection();
  };

  clearSelection(event) {
    if (event) event.preventDefault();

    this.dismissSelectedUser({ resetSearch: true });
  }

  dismissSelectedUser({ resetSearch = false } = {}) {
    if (resetSearch) {
      this.inputTarget.value = '';
    }

    if (this.hasSelectedUserIdTarget) {
      this.selectedUserIdTarget.value = '';
    }

    this.hideSelectedUserCard();
    this.hideMissingFieldsNotice();
    this.hideMembershipNotices();
    if (this.hasExistingUserNoticeTarget) {
      this.existingUserNoticeTarget.hidden = true;
    }
    this.showNewUserSection();
    this.resetSearchSubmitButton();
  }

  commit(selected) {
    const userIdValue = selected.getAttribute('data-autocomplete-user-id-value');
    const userTypeValue = selected.getAttribute('data-autocomplete-user-type-value');
    const memberOfThisCompany = selected.dataset.autocompleteMemberOfThisCompanyValue === 'true';
    const memberOfOtherCompany = selected.dataset.autocompleteMemberOfOtherCompanyValue === 'true';

    this.isCommitting = true;

    try {
      super.commit(selected);

      if (this.hasSelectedUserIdTarget) {
        this.selectedUserIdTarget.value = userIdValue || '';
      }

      if (this.hasEmailFieldTarget) {
        this.emailFieldTarget.value = selected.getAttribute('data-autocomplete-value') || '';
        this.emailFieldTarget.disabled = true;
      }

      FILLABLE_FIELDS.forEach(({ name, attr }) => {
        const value = selected.getAttribute(`data-autocomplete-${attr}-value`);
        this.updateCardField(name, value);
      });

      this.showSelectedUserCard();
      this.updateMembershipNotices(memberOfThisCompany, memberOfOtherCompany);
      this.updateSearchSubmitButton(userTypeValue, memberOfThisCompany);
      this.syncNoticesAndSubmit();
      this.hideNewUserSection();
    } finally {
      this.isCommitting = false;
    }
  }

  updateCardField(fieldName, value) {
    const fieldInput = this[`${fieldName}FieldTarget`];
    const isBlank = !value || !String(value).trim();

    if (!fieldInput) return;

    fieldInput.value = isBlank ? '' : value;
    fieldInput.disabled = !isBlank;
    this.updateFieldValidationStates();
  }

  validateMissingFields() {
    this.syncNoticesAndSubmit();
  }

  syncNoticesAndSubmit() {
    const hasVisibleMissingInputs = this.hasVisibleMissingFieldInputs();
    const hasEmptyVisibleInputs = this.hasEmptyVisibleFieldInputs();

    if (this.hasMissingFieldsNoticeTarget) {
      this.missingFieldsNoticeTarget.hidden = !hasVisibleMissingInputs;
    }

    if (this.hasExistingUserNoticeTarget) {
      this.existingUserNoticeTarget.hidden = hasVisibleMissingInputs;
    }

    if (this.hasSearchSubmitButtonTarget && !this.memberOfThisCompanyBlocked()) {
      this.searchSubmitButtonTarget.disabled = hasEmptyVisibleInputs;
    }

    this.updateFieldValidationStates();
  }

  updateFieldValidationStates() {
    FILLABLE_FIELDS.forEach(({ name }) => {
      const fieldInput = this[`${name}FieldTarget`];
      if (!fieldInput) return;

      if (fieldInput.disabled) {
        fieldInput.removeAttribute('aria-invalid');
        fieldInput.removeAttribute('required');
        return;
      }

      const isEmpty = !fieldInput.value.trim();
      fieldInput.toggleAttribute('required', true);

      if (isEmpty) {
        fieldInput.setAttribute('aria-invalid', 'true');
      } else {
        fieldInput.removeAttribute('aria-invalid');
      }
    });
  }

  hasVisibleMissingFieldInputs() {
    return FILLABLE_FIELDS.some(({ name }) => {
      const fieldInput = this[`${name}FieldTarget`];
      return fieldInput && !fieldInput.disabled;
    });
  }

  hasEmptyVisibleFieldInputs() {
    return FILLABLE_FIELDS.some(({ name }) => {
      const fieldInput = this[`${name}FieldTarget`];
      return fieldInput && !fieldInput.disabled && !fieldInput.value.trim();
    });
  }

  memberOfThisCompanyBlocked() {
    return this.hasMemberOfThisCompanyNoticeTarget && !this.memberOfThisCompanyNoticeTarget.hidden;
  }

  hideMissingFieldsNotice() {
    if (this.hasMissingFieldsNoticeTarget) {
      this.missingFieldsNoticeTarget.hidden = true;
    }
  }

  showSelectedUserCard() {
    if (this.hasSelectedUserCardTarget) {
      this.selectedUserCardTarget.hidden = false;
    }
  }

  hideSelectedUserCard() {
    if (this.hasSelectedUserCardTarget) {
      this.selectedUserCardTarget.hidden = true;
    }
  }

  updateMembershipNotices(memberOfThisCompany, memberOfOtherCompany) {
    this.hideMembershipNotices();

    if (memberOfThisCompany && this.hasMemberOfThisCompanyNoticeTarget) {
      this.memberOfThisCompanyNoticeTarget.hidden = false;
    } else if (memberOfOtherCompany && this.hasMemberOfOtherCompanyNoticeTarget) {
      this.memberOfOtherCompanyNoticeTarget.hidden = false;
    }
  }

  hideMembershipNotices() {
    if (this.hasMemberOfThisCompanyNoticeTarget) {
      this.memberOfThisCompanyNoticeTarget.hidden = true;
    }
    if (this.hasMemberOfOtherCompanyNoticeTarget) {
      this.memberOfOtherCompanyNoticeTarget.hidden = true;
    }
  }

  updateSearchSubmitButton(userType, memberOfThisCompany = false) {
    if (!this.hasSearchSubmitButtonTarget) return;

    if (memberOfThisCompany) {
      this.searchSubmitButtonTarget.disabled = true;
    }

    this.searchSubmitButtonTarget.textContent = CONVERSION_USER_TYPES.includes(userType)
      ? this.convertAccountLabelValue
      : this.addLabelValue;
  }

  resetSearchSubmitButton() {
    if (!this.hasSearchSubmitButtonTarget) return;

    this.searchSubmitButtonTarget.disabled = false;
    this.searchSubmitButtonTarget.textContent = this.addLabelValue;
  }

  hideNewUserSection() {
    if (!this.hasNewUserSectionTarget) return;

    this.newUserSectionTarget.hidden = true;
  }

  showNewUserSection() {
    if (!this.hasNewUserSectionTarget) return;

    this.newUserSectionTarget.hidden = false;
  }
}
